import { Badge, Box, Button, Group, Loader, Menu, Paper, Select, Stack, Tabs, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import {
  IconArchive,
  IconChevronDown,
  IconClockShare,
  IconCopy,
  IconHexagons,
  IconSend2,
  IconSettingsCog,
  IconTrash,
} from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { MRT_ColumnDef, MRT_Row } from 'mantine-react-table'
import { createMRTColumnHelper } from 'mantine-react-table'
import { observer } from 'mobx-react-lite'
import { usePostHog } from 'posthog-js/react'
import { parse } from 'qs'
import { createContext, Fragment, useContext, useEffect, useMemo, useState } from 'react'
import { useSearch } from 'wouter'
import { navigate } from 'wouter/use-browser-location'

import { Screen } from '@/components/Screen'
import type { rowActionsIconRegistry, TableProps } from '@/components/Table'
import { HeaderCellWithTooltip, Table } from '@/components/Table'
import { UserDisplay } from '@/components/UserDisplay'
import { theme } from '@/configs/theme'
import { MSK_NAME, MSK_TOPIC } from '@/constants/contentModules'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useSetupProgressTracker } from '@/hooks/useSetupProgressTracker'
import type {
  GetContentModuleResponse,
  GetContentModulesResponse,
  GetESKsResponse,
  GetUsersResponseEntry,
} from '@/services/api/api.types'
import type { ContentModuleStatus } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import {
  canSponsorAdminDelete,
  canSponsorAdminEdit,
  canSponsorAdminMoveToDraft,
  canSponsorAdminMoveToPendingApproval,
  canSponsorAdminMoveToTesting,
  canSponsorAdminPublish,
  type CMTableEntry,
  smeTables,
  sponsorAdminTables,
} from '@/utils/contentModuleTables'
import { useNavigateTo } from '@/utils/navigation'
import { getRoutePath } from '@/utils/navigation'

import { PriorityScreen } from './PriorityScreen'

type RowActionsAndLabels = {
  actions: Array<keyof typeof rowActionsIconRegistry>
  labels: Partial<Record<keyof typeof rowActionsIconRegistry, string>>
}

const isMyModule = (cm: GetContentModuleResponse, userId: string): boolean => {
  return cm.createdBy === userId
}

const rowActionsAndLabels = (
  row: MRT_Row<GetContentModuleResponse>,
  userId: string,
  isSponsorAdmin: boolean,
): RowActionsAndLabels => {
  // Can a user edit or just view this module?
  const editable = isSponsorAdmin
    ? canSponsorAdminEdit(
        row.original.status as ContentModuleStatus,
        isMyModule(row.original, userId),
        row.original.allowedForAllSponsors ?? false,
        row.original.visibleToMsk ?? false,
        row.original.editable,
      )
    : isMyModule(row.original, userId)

  if (editable) {
    return {
      actions: row.original.status === 'draft' || row.original.status === 'testing' ? ['edit', 'view'] : ['view'],
      labels: { edit: 'Edit Module', view: 'View module' },
    }
  }
  // Cannot edit modules that the user did not create
  return { actions: ['view'], labels: { view: 'View module' } }
}

type TableContext = {
  esks: GetESKsResponse
  changeModuleStatus: (data: GetContentModulesResponse[0], status: `${ContentModuleStatus}`) => void
  copyContentModule: (taskId: string) => void
  sponsorMskCms: GetContentModuleResponse[]
  users: GetUsersResponseEntry[]
}
const TableContext = createContext<TableContext>({} as TableContext)
const useTableContext = () => useContext(TableContext)

// ContentModulesScreen Component
// --------------------------------------------------------------------------------
export const ContentModulesScreen = observer(function ContentModulesScreen() {
  const { api, authenticationStore, uiStore, devStore } = useGlobalState()
  const { user, isSponsorAdmin } = authenticationStore
  const posthog = usePostHog()
  const [isLoading, setIsLoading] = useState(false)
  const isMobile = useCheckMobileScreen()

  // Get setup progress tracker for marking completion
  const { markStepComplete } = useSetupProgressTracker()
  const [activeTab, setActiveTab] = useState<string | null>(isSponsorAdmin ? 'all-modules' : 'my-modules')
  const searchParamsString = useSearch()
  const searchParams: { tab?: string } = parse(searchParamsString)

  // Set active tab from URL parameter if provided
  useEffect(() => {
    if (searchParams.tab) {
      setActiveTab(searchParams.tab)
    }
  }, [searchParams.tab])

  useEffect(() => {
    posthog?.capture('$pageview', { skPlatform: 'portal' })
  }, [posthog])
  const navigateTo = useNavigateTo()

  const esksQuery = useQuery({
    queryKey: ['cm', 'esks'],
    queryFn: api.getESKs,
  })

  const smeUsersQuery = useQuery({
    queryKey: ['users', 'sme'],
    queryFn: () => api.getUsers({ page: 1, pageSize: 100, role: 'SME' }),
    enabled: isSponsorAdmin,
  })

  const adminUsersQuery = useQuery({
    queryKey: ['users', 'admin'],
    queryFn: () => api.getUsers({ page: 1, pageSize: 100, role: 'Admin' }),
    enabled: isSponsorAdmin,
  })

  const swiAdminUsersQuery = useQuery({
    queryKey: ['users', 'swi-admin'],
    queryFn: () => api.getUsers({ page: 1, pageSize: 100, role: 'SWI Admin' }),
    enabled: isSponsorAdmin,
  })

  const allContentCreators = useMemo(() => {
    // SMEs don't have access to user data, so return empty array
    if (!isSponsorAdmin) {
      return []
    }

    const allUsers = [
      ...(smeUsersQuery.data?.members ?? []),
      ...(adminUsersQuery.data?.members ?? []),
      ...(swiAdminUsersQuery.data?.members ?? []),
    ]

    // Remove duplicates based on userId
    const uniqueUsers = allUsers.filter(
      (user, index, array) => array.findIndex(u => u.userId === user.userId) === index,
    )

    return uniqueUsers
  }, [isSponsorAdmin, smeUsersQuery.data, adminUsersQuery.data, swiAdminUsersQuery.data])

  const usersLoaded = isSponsorAdmin
    ? smeUsersQuery.isSuccess && adminUsersQuery.isSuccess && swiAdminUsersQuery.isSuccess
    : true // SMEs don't need to load user data

  const cmQuery = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules(),
    initialData: [],
    enabled: esksQuery.isSuccess && usersLoaded,
    refetchInterval: 7 * 1000,
  })

  const cmChangeStatusMutation = useMutation({
    mutationFn: api.changeContentModuleStatus,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => uiStore.hideLoadingOverlay(),
    onSuccess: (d, _vars) => {
      const success = 'status' in d && d.status
      const error = 'errors' in d

      if (success) {
        api.queryClient.invalidateQueries({
          queryKey: ['cm', 'list'],
        })

        setTimeout(() => {
          cmQuery.refetch()
        }, 1000)

        // Mark createContent step complete if this was a publish action
        if (_vars.status === 'published') {
          markStepComplete('createContent')
        }

        // DISABLED: As per request from Jeff: https://infinitered.slack.com/archives/C07MY1RQ63S/p1734730558931089?thread_ts=1734729429.572509&cid=C07MY1RQ63S
        // notifications.show({
        //   title: 'Module Status Updated',
        //   message: d.message ?? `The module's status is now "${vars.status}".`,
        //   color: 'green',
        //   classNames: commonClasses.notificationFilled,
        //   position: 'bottom-left',
        // })
      } else if (error) {
        const errorMessage =
          d.errors?.map((e, i) => (
            <Fragment key={i}>
              <br />
              {e.error}
            </Fragment>
          )) ?? 'There was an issue attempting to update the module status. Please try again or contact support.'

        notifications.show({
          title: 'Could Not Update Module Status',
          message: errorMessage,
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
    },
    onError: () => {
      notifications.show({
        title: 'Could Not Update Module Status',
        message: 'There was an issue attempting to update the module status. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const copyContentModuleMutation = useMutation({
    mutationFn: api.copyContentModule,
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
      notifications.show({
        title: 'Module Copied',
        message: 'The module was successfully copied.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
    onError: () => {
      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue copying the module. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  function copyContentModule(taskId: string) {
    copyContentModuleMutation.mutate({ task_id: taskId })
  }

  function changeModuleStatus(data: GetContentModulesResponse[0], status: `${ContentModuleStatus}`) {
    const mutate = () => cmChangeStatusMutation.mutate({ task_id: data.taskId!, status })

    if (status === 'published') {
      mutate()
      return
    }

    if (status === 'draft' || status === 'removed' || status === 'testing') {
      const title = status === 'draft' ? 'Unpublish Module?' : status === 'testing' ? 'Test Module?' : 'Delete Module?'
      const message =
        status === 'draft'
          ? 'Are you sure you want to change this module\'s status to "Draft"?'
          : status === 'testing'
            ? 'You—and only you—can now interact with your content in the Sidekick app. No one else will be able to interact with this content until you publish it.'
            : 'Are you sure you want to delete this module? This action cannot be undone.'
      const color = status === 'draft' ? 'orange' : status === 'testing' ? undefined : 'red'
      const label = status === 'draft' ? 'Change to Draft' : status === 'testing' ? 'Test in App' : 'Delete Module'

      modals.openConfirmModal({
        title,
        centered: true,
        children: <Text size="sm">{message}</Text>,
        labels: { confirm: label, cancel: 'Cancel' },
        confirmProps: { color },
        groupProps: { mt: 'xl' },
        onConfirm: mutate,
      })

      return
    }

    if (status === 'pending_approval') {
      // Skip confirmations for Sponsor Admin when moving from published state
      if (isSponsorAdmin && data.status === 'published') {
        mutate()
        return
      }

      modals.openConfirmModal({
        title: 'Ready to Submit?',
        centered: true,
        children: <Text size="sm">{`Are you ready to submit for approval?`}</Text>,
        labels: { confirm: 'Yes', cancel: 'Cancel' },
        groupProps: { mt: 'xl' },
        onCancel: modals.closeAll,
        onConfirm: () => {
          modals.openConfirmModal({
            title: 'Copyright Confirmation',
            centered: true,
            children: (
              <Text size="sm">{`I verify that all the information provided for this module belongs to me and my organization or is used with permission from the owner.`}</Text>
            ),
            labels: { confirm: 'Confirm and Submit', cancel: 'Cancel' },
            groupProps: { mt: 'xl' },
            onCancel: modals.closeAll,
            onConfirm: mutate,
          })
        },
      })
    }
  }

  const data = useMemo(() => {
    if (!cmQuery.data) return []
    return devStore.showRemovedModules ? cmQuery.data : cmQuery.data.filter(d => d.status !== 'removed')
  }, [cmQuery.data, devStore.showRemovedModules])

  const cmCreateMutation = useMutation({
    mutationFn: api.createContentModule,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => {
      uiStore.hideLoadingOverlay()
      setIsLoading(false)
    },
    onSuccess: data => {
      navigate(getRoutePath('contentModuleFormEdit', { id: data.taskId }), { replace: true })
      /*
      notifications.show({
        title: 'Module Created',
        message: 'The module was successfully created.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      */
    },
    onError: () => {
      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue creating the module. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const CommonTableProps: Omit<TableProps<GetContentModulesResponse[0]>, 'data' | 'columns'> = {
    entity: { singular: 'Module', plural: 'Modules', Icon: IconHexagons },
    enableSorting: true,
    initialState: { sorting: [{ id: ColId.Status, desc: true }] },
    positionActionsColumn: 'first',
    EmptyStateProps: { py: 'xl' },
    onRowActionClick: (action, row) => {
      if (action === 'edit') {
        posthog?.capture('content_module_edit', { taskId: row.original.taskId!, skPlatform: 'portal' })
        navigateTo('contentModuleFormEdit', { id: row.original.taskId! })
        uiStore.setIsNewContentModule(false)
      } else if (action === 'view') {
        posthog?.capture('content_module_view', { taskId: row.original.taskId!, skPlatform: 'portal' })
        navigateTo('contentModule', { id: row.original.taskId! })
      }
    },
    onToolbarActionClick: action => {
      if (action === 'new' && !isLoading) {
        setIsLoading(true)
        cmCreateMutation?.mutateAsync({ content: ' ' })
        uiStore.setIsNewContentModule(true)
      }
    },
  }

  const getTables = (isSponsorAdminValue: boolean): CMTableEntry[] => {
    return isSponsorAdminValue ? sponsorAdminTables : smeTables
  }

  // Dropdown options for mobile view
  const tabOptions = useMemo(() => {
    const tables = getTables(isSponsorAdmin)
    const options = tables.map(entry => ({
      value: entry.title.toLowerCase().replace(' ', '-'),
      label: `${entry.title} (${data.filter(item => entry.filterFn(item, user?.userId === item.createdBy)).length})`,
    }))

    // Add Module Prioritization option
    options.push({
      value: 'cm-prioritization',
      label: 'Module Prioritization',
    })

    return options
  }, [isSponsorAdmin, data, user?.userId])

  const sponsorMskCms = useMemo(() => {
    return data.filter(cm => cm.visibleToMsk && !cm.allowedForAllSponsors)
  }, [data])

  const sponsorAdminText = `The content library contains all the content modules in your Sidekick account.`

  const smeText = `The content library contains all the content modules in your organization's Sidekick account.`

  return (
    <Screen title="Content Library" icon={IconHexagons}>
      <Stack gap="md">
        <Text size="md" c="black" mb="md" style={{ whiteSpace: 'pre-line' }}>
          {isSponsorAdmin ? sponsorAdminText : smeText}
        </Text>
        <TableContext.Provider
          value={{
            changeModuleStatus,
            copyContentModule,
            esks: esksQuery.data ?? [],
            sponsorMskCms,
            users: allContentCreators,
          }}>
          {isMobile ? (
            <>
              <Select
                value={activeTab}
                onChange={setActiveTab}
                data={tabOptions}
                size="lg"
                fw={500}
                placeholder="Select a section"
              />

              {/* Tab panels for mobile */}
              <div style={{ paddingTop: '1rem' }}>
                {getTables(isSponsorAdmin).map(entry => {
                  const tabValue = entry.title.toLowerCase().replace(' ', '-')
                  const isActive = activeTab === tabValue

                  return isActive ? (
                    <Paper key={entry.title} radius="md" p="lg" shadow="sm">
                      <Table
                        data={data.filter(item => entry.filterFn(item, user?.userId === item.createdBy))}
                        columns={cols}
                        toolbarActionsInclude={entry.toolbarActionsInclude}
                        rowActionsInclude={row => rowActionsAndLabels(row, user?.userId ?? '', isSponsorAdmin).actions}
                        rowActionsTooltipLabels={row =>
                          rowActionsAndLabels(row, user?.userId ?? '', isSponsorAdmin).labels
                        }
                        displayColumnDefOptions={entry.displayColumnDefOptions}
                        {...CommonTableProps}
                      />
                    </Paper>
                  ) : null
                })}

                {/* Module Prioritization panel for mobile */}
                {activeTab === 'cm-prioritization' && <PriorityScreen />}
              </div>
            </>
          ) : (
            <Tabs value={activeTab} onChange={setActiveTab}>
              <Tabs.List>
                {getTables(isSponsorAdmin).map(entry => (
                  <Tabs.Tab
                    key={entry.title}
                    value={entry.title.toLowerCase().replace(' ', '-')}
                    styles={{
                      tab: {
                        fontSize: '1.1rem',
                        fontWeight: 500,
                      },
                    }}>
                    {`${entry.title} (${data.filter(item => entry.filterFn(item, user?.userId === item.createdBy)).length})`}
                  </Tabs.Tab>
                ))}
                <Tabs.Tab
                  key="cm-prioritization"
                  value="cm-prioritization"
                  styles={{ tab: { fontSize: '1.1rem', fontWeight: 500 } }}>
                  Module Prioritization
                </Tabs.Tab>
              </Tabs.List>

              {/* Tab panels for desktop */}
              {getTables(isSponsorAdmin).map(entry => (
                <Tabs.Panel key={entry.title} value={entry.title.toLowerCase().replace(' ', '-')} pt="md">
                  <Paper radius="md" p="lg" shadow="sm">
                    <Table
                      data={data.filter(item => entry.filterFn(item, user?.userId === item.createdBy))}
                      columns={cols}
                      toolbarActionsInclude={entry.toolbarActionsInclude}
                      rowActionsInclude={row => rowActionsAndLabels(row, user?.userId ?? '', isSponsorAdmin).actions}
                      rowActionsTooltipLabels={row =>
                        rowActionsAndLabels(row, user?.userId ?? '', isSponsorAdmin).labels
                      }
                      displayColumnDefOptions={entry.displayColumnDefOptions}
                      {...CommonTableProps}
                    />
                  </Paper>
                </Tabs.Panel>
              ))}
              <Tabs.Panel key="cm-prioritization" value="cm-prioritization" pt="md">
                <PriorityScreen />
              </Tabs.Panel>
            </Tabs>
          )}
        </TableContext.Provider>
      </Stack>
    </Screen>
  )
})

// Table Columns and Menu Actions
// --------------------------------------------------------------------------------
enum ColId {
  Title = 'title',
  Topic = 'topic',
  SubTopic = 'sub-topic',
  EskName = 'esk-name',
  Status = 'status',
  HumanExpert = 'human-expert',
}

const columnHelper = createMRTColumnHelper<GetContentModulesResponse[0]>()

const cols: MRT_ColumnDef<GetContentModulesResponse[0]>[] = [
  columnHelper.accessor('title', {
    id: ColId.Title,
    header: 'Title',
    grow: true,
  }),
  columnHelper.accessor('status', {
    id: ColId.Status,
    header: 'Status',
    grow: false,
    size: 160,
    Cell: props => {
      const status = props.cell.getValue() ?? 'draft'
      const inProcess = props.row.original.inProcess
      const { changeModuleStatus, copyContentModule, sponsorMskCms } = useTableContext()
      const { authenticationStore } = useGlobalState()
      const { user, isSponsorAdmin } = authenticationStore

      const enableActionsMenu = isSponsorAdmin || props.row.original.editable

      const isAllSponsorsModule = props.row.original.allowedForAllSponsors ?? false
      const createdBy = props.row.original.createdBy
      const isMyModule = createdBy === user?.userId
      const isVisibleToMsk = props.row.original.visibleToMsk ?? false

      const menuIconStyle = { width: theme.rem(18), height: theme.rem(18) }
      const changeStatus = (status: `${ContentModuleStatus}`) => () => changeModuleStatus(props.row.original, status)
      const taskId = props.row.original.taskId ?? ''
      const copyModule = () => copyContentModule(taskId)

      const statusBadge = (
        <Button
          variant="filled"
          size="xs"
          color={
            {
              published: 'green',
              draft: 'orange',
              removed: 'gray',
              pending_approval: 'blue',
              testing: 'yellow',
              complete: 'blue', // TODO: remove once backend implements `pending_approval` status
            }[status]
          }
          rightSection={<IconChevronDown size={16} stroke={1.5} />}
          pr={12}
          radius="md">
          {
            // @ts-expect-error - `complete` status is deprecated
            (status === 'complete' || status === 'pending_approval' ? 'Pending' : status).toUpperCase()
          }
        </Button>
      )

      return (
        <Group gap="xs" wrap="nowrap">
          {inProcess ? (
            <Badge variant="filled" color="gray" rightSection={<Loader size="xs" color="white" type="dots" />}>
              PROCESSING
            </Badge>
          ) : enableActionsMenu ? (
            <Menu withArrow arrowPosition="center">
              <Menu.Target>
                <Box style={{ cursor: 'pointer' }}>{statusBadge}</Box>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Change Status:</Menu.Label>
                {isSponsorAdmin ? (
                  <>
                    <Menu.Item
                      leftSection={<IconArchive style={menuIconStyle} />}
                      disabled={
                        !canSponsorAdminMoveToDraft(
                          status as ContentModuleStatus,
                          isMyModule,
                          isAllSponsorsModule,
                          isVisibleToMsk,
                        )
                      }
                      onClick={changeStatus('draft')}>
                      Back to Draft
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconClockShare style={menuIconStyle} />}
                      disabled={
                        !canSponsorAdminMoveToPendingApproval(
                          status as ContentModuleStatus,
                          isMyModule,
                          isAllSponsorsModule,
                          isVisibleToMsk,
                        )
                      }
                      onClick={changeStatus('pending_approval')}>
                      Back to Pending
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconSettingsCog style={menuIconStyle} />}
                      disabled={
                        !canSponsorAdminMoveToTesting(
                          status as ContentModuleStatus,
                          isMyModule,
                          isAllSponsorsModule,
                          isVisibleToMsk,
                        )
                      }
                      onClick={changeStatus('testing')}>
                      Test in App
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconSend2 style={menuIconStyle} />}
                      disabled={
                        !canSponsorAdminPublish(
                          status as ContentModuleStatus,
                          isMyModule,
                          isAllSponsorsModule,
                          isVisibleToMsk,
                        )
                      }
                      onClick={changeStatus('published')}>
                      Publish
                    </Menu.Item>
                    <Menu.Item
                      color="red.7"
                      leftSection={<IconTrash style={menuIconStyle} />}
                      disabled={
                        !canSponsorAdminDelete(
                          status as ContentModuleStatus,
                          isMyModule,
                          isAllSponsorsModule,
                          isVisibleToMsk,
                          sponsorMskCms,
                        )
                      }
                      onClick={changeStatus('removed')}>
                      Delete
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconCopy style={menuIconStyle} />}
                      disabled={!isMyModule}
                      onClick={copyModule}>
                      Copy
                    </Menu.Item>
                  </>
                ) : (
                  <>
                    <Menu.Item
                      leftSection={<IconArchive style={menuIconStyle} />}
                      disabled={status === 'draft' || status === 'pending_approval' || status === 'published'}
                      onClick={changeStatus('draft')}>
                      Back to Draft
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconSettingsCog style={menuIconStyle} />}
                      disabled={status === 'testing' || status === 'pending_approval' || status === 'published'}
                      onClick={changeStatus('testing')}>
                      Test in App
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconClockShare style={menuIconStyle} />}
                      disabled={status === 'pending_approval' || status === 'published'}
                      onClick={changeStatus('pending_approval')}>
                      Submit for Approval
                    </Menu.Item>
                    <Menu.Item
                      color="red.7"
                      leftSection={<IconTrash style={menuIconStyle} />}
                      disabled={status === 'removed' || status === 'pending_approval' || status === 'published'}
                      onClick={changeStatus('removed')}>
                      Delete
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconCopy style={menuIconStyle} />}
                      disabled={!isMyModule}
                      onClick={copyModule}>
                      Copy
                    </Menu.Item>
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
          ) : (
            statusBadge
          )}
        </Group>
      )
    },
  }),
  columnHelper.accessor(
    row => {
      if (row.visibleToMsk) {
        return MSK_TOPIC
      }
      return row.categories?.[0] ?? ''
    },
    {
      id: ColId.Topic,
      header: 'Topic',
      grow: false,
    },
  ),
  // columnHelper.accessor(row => row.mappings?.join(', ') ?? '', {
  //   id: ColId.SubTopic,
  //   header: 'Sub-Topic',
  //   grow: false,
  // }),
  columnHelper.accessor(
    row => {
      if (row.visibleToMsk) {
        return MSK_NAME
      }
      const { esks } = useTableContext()
      const selectEsk = row.selectEsk

      // Handle PSK case - show "Personal Sidekick" instead of "PSK"
      if (selectEsk === 'PSK') {
        return MSK_NAME
      }

      return esks.find(e => e.eskId === selectEsk)?.name ?? `${selectEsk ?? ''}`
    },
    {
      id: ColId.EskName,
      header: '',
      grow: false,
      size: 138,
      Header: ({ column }) => (
        <HeaderCellWithTooltip column={column} header={<>Sidekick</>} tooltip="The Sidekick assigned to a module" />
      ),
    },
  ),
  columnHelper.accessor(
    row => {
      const { users } = useTableContext()
      const createdBy = row.createdBy
      if (!createdBy) return { display: '', name: '', email: '' }

      const user = users.find(u => u.userId === createdBy)
      if (!user) {
        const fallback = row.owner ?? 'Unknown'
        return { display: fallback, name: fallback, email: '' }
      }

      return {
        display: user.name || user.email,
        name: user.name || '',
        email: user.email || '',
      }
    },
    {
      id: ColId.HumanExpert,
      header: 'Owner',
      grow: false,
      size: 260,
      Cell: ({ cell }) => {
        const { isSponsorAdmin } = useGlobalState().authenticationStore
        const value = cell.getValue<{ display: string; name: string; email: string }>()

        return <UserDisplay user={value} showDetailedView={isSponsorAdmin} />
      },
    },
  ),
]
